import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatPHP } from "@/lib/api";
import { PageHeader, Stat, Surface } from "@/components/app/Primitives";
import { Users, Wallet, Clock, FileText, ShieldCheck, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import StatusBadge from "@/components/app/StatusBadge";
import InstallPWA from "@/components/app/InstallPWA";
import WageOrderBanner from "@/components/app/WageOrderBanner";
import AnnouncementBoard from "@/components/app/AnnouncementBoard";

const COLORS = ["#0F4C81", "#1E6BB8", "#3B82F6", "#60A5FA", "#93C5FD", "#BFDBFE", "#1E40AF"];

// HR heads-up: contracts expiring within 6 / 3 / 1 months (or already expired).
function ContractAlertsBanner() {
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState(null);
    useEffect(() => { api.get("/contracts/alerts").then((r) => setAlerts(r.data)).catch(() => {}); }, []);
    if (!alerts || !alerts.total) return null;
    const part = (arr, label) => arr.length ? `${arr.length} ${label}` : null;
    const parts = [part(alerts.expired, "expired"), part(alerts["1_month"], "within 1 month"),
                   part(alerts["3_months"], "within 3 months"), part(alerts["6_months"], "within 6 months")].filter(Boolean);
    return (
        <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-center justify-between gap-3 cursor-pointer hover:bg-amber-100/70"
            onClick={() => navigate("/hris/contracts")} data-testid="contract-alerts-banner">
            <div className="text-sm text-amber-900">
                <b>📜 {alerts.total} employee contract{alerts.total > 1 ? "s" : ""} expiring</b> — {parts.join(" · ")}
            </div>
            <span className="text-xs font-semibold text-amber-900 shrink-0">Review →</span>
        </div>
    );
}

const GEN_COLORS = { "Gen Z (1997–2012)": "#10B981", "Millennials (1981–1996)": "#0F4C81",
    "Gen X (1965–1980)": "#F59E0B", "Boomers (1946–1964)": "#8B5CF6", "Unspecified": "#94A3B8" };
const GENDER_COLORS = { Male: "#1E6BB8", Female: "#EC4899", Unspecified: "#94A3B8" };

function Chip({ active, onClick, children }) {
    return (
        <button onClick={onClick}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                active ? "bg-[#0F4C81] text-white border-[#0F4C81]"
                       : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {children}
        </button>
    );
}

// Workforce demographics — generations, gender split, generation × gender.
function DemographicsSection() {
    const [demo, setDemo] = useState(null);
    useEffect(() => { api.get("/insights/demographics").then((r) => setDemo(r.data)).catch(() => {}); }, []);
    if (!demo) return null;
    const genderData = (demo.gender || []).map((g) => ({ name: g.label, value: g.count }));
    return (
        <Surface className="mt-3 p-4" data-testid="demographics-section">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-semibold">Workforce Demographics</h3>
                <div className="text-xs text-muted-foreground">
                    {demo.total} active employees{demo.average_age ? ` · avg age ${demo.average_age}` : ""}
                </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                {(demo.generations || []).filter((g) => g.key !== "unspecified").map((g) => (
                    <div key={g.key} className="rounded-lg border border-slate-200 p-3">
                        <div className="text-xs text-muted-foreground">{g.label}</div>
                        <div className="text-2xl font-bold" style={{ color: GEN_COLORS[g.label] || "#0F4C81" }}>{g.count}</div>
                        <div className="text-[11px] text-muted-foreground">{g.pct}% of workforce</div>
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-1">Gender split</div>
                    <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                            <Pie data={genderData} dataKey="value" nameKey="name" innerRadius={42} outerRadius={70} paddingAngle={2}>
                                {genderData.map((d, i) => <Cell key={i} fill={GENDER_COLORS[d.name] || COLORS[i]} />)}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="flex justify-center gap-4 text-xs">
                        {genderData.map((d) => (
                            <span key={d.name} className="flex items-center gap-1.5">
                                <span className="size-2 rounded-full" style={{ background: GENDER_COLORS[d.name] || "#999" }} />
                                {d.name} <b className="font-mono">{d.value}</b>
                            </span>
                        ))}
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <div className="text-xs font-semibold text-muted-foreground mb-1">Generation × gender</div>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={demo.matrix || []} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="#e5e7eb" vertical={false} />
                            <XAxis dataKey="generation" tick={{ fontSize: 10 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="male" name="Male" stackId="g" fill={GENDER_COLORS.Male} />
                            <Bar dataKey="female" name="Female" stackId="g" fill={GENDER_COLORS.Female} radius={[3, 3, 0, 0]} />
                            <Bar dataKey="unspecified" name="Unspecified" stackId="g" fill={GENDER_COLORS.Unspecified} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </Surface>
    );
}

const METRICS = {
    late_count: { label: "Late incidents", key: "late_count", per: "late_per_head" },
    late_minutes: { label: "Late minutes", key: "late_minutes", per: "avg_late_minutes" },
    absences: { label: "Absences", key: "absences", per: "absences_per_head" },
};

// Tardiness & absence explorer — toggle dimension / metric / period.
function TardinessExplorer() {
    const navigate = useNavigate();
    const [dimension, setDimension] = useState("generation");
    const [metric, setMetric] = useState("late_count");
    const [months, setMonths] = useState(3);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setLoading(true);
        api.get(`/insights/tardiness?dimension=${dimension}&months=${months}`)
            .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, [dimension, months]);

    const m = METRICS[metric];
    const rows = [...(data?.rows || [])].sort((a, b) => b[m.key] - a[m.key]);
    const top = rows[0];
    const totalMetric = rows.reduce((s, r) => s + r[m.key], 0);
    const insight = top && totalMetric > 0
        ? `${top.label} lead${top.label.endsWith("s") ? "" : "s"} in ${m.label.toLowerCase()} — ${top[m.key].toLocaleString()} (${Math.round(top[m.key] * 100 / totalMetric)}% of the total) across ${top.headcount} employee${top.headcount === 1 ? "" : "s"}.`
        : "No incidents in this window.";

    return (
        <Surface className="mt-3 p-4" data-testid="tardiness-explorer">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <h3 className="font-display font-semibold">Tardiness &amp; Absence Explorer</h3>
                <div className="text-xs text-muted-foreground">{data ? `${data.window.from} → ${data.window.to}` : ""}</div>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
                <div className="flex items-center gap-1.5">
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">View by</span>
                    <Chip active={dimension === "generation"} onClick={() => setDimension("generation")}>Generation</Chip>
                    <Chip active={dimension === "gender"} onClick={() => setDimension("gender")}>Gender</Chip>
                    <Chip active={dimension === "department"} onClick={() => setDimension("department")}>Department</Chip>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Metric</span>
                    <Chip active={metric === "late_count"} onClick={() => setMetric("late_count")}>Late incidents</Chip>
                    <Chip active={metric === "late_minutes"} onClick={() => setMetric("late_minutes")}>Late minutes</Chip>
                    <Chip active={metric === "absences"} onClick={() => setMetric("absences")}>Absences</Chip>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Period</span>
                    <Chip active={months === 1} onClick={() => setMonths(1)}>1 mo</Chip>
                    <Chip active={months === 3} onClick={() => setMonths(3)}>3 mo</Chip>
                    <Chip active={months === 6} onClick={() => setMonths(6)}>6 mo</Chip>
                </div>
            </div>
            <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-sm text-blue-900 mb-3" data-testid="tardiness-insight">
                💡 {loading ? "Crunching…" : insight}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ResponsiveContainer width="100%" height={230}>
                    <BarChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke="#e5e7eb" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v, n) => [v.toLocaleString(), METRICS[metric].label]}
                                 labelFormatter={(l) => l} />
                        <Bar dataKey={m.key} name={m.label} radius={[3, 3, 0, 0]}>
                            {rows.map((r, i) => <Cell key={i} fill={GEN_COLORS[r.label] || GENDER_COLORS[r.label] || COLORS[i % COLORS.length]} />)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
                <div className="overflow-x-auto">
                    <table className="w-full dense-table">
                        <thead>
                            <tr>
                                <th>{dimension === "department" ? "Department" : dimension === "gender" ? "Gender" : "Generation"}</th>
                                <th className="num">Headcount</th>
                                <th className="num">Lates</th>
                                <th className="num">Late rate</th>
                                <th className="num">Avg late (min)</th>
                                <th className="num">Absences</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.key}>
                                    <td className="font-medium">{r.label}</td>
                                    <td className="num">{r.headcount}</td>
                                    <td className="num">{r.late_count}</td>
                                    <td className="num">{r.late_rate_pct}%</td>
                                    <td className="num">{r.avg_late_minutes}</td>
                                    <td className="num">{r.absences}</td>
                                </tr>
                            ))}
                            {!rows.length && <tr><td colSpan={6} className="text-center text-muted-foreground py-4">No data in this window.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-1">🏃 Top latecomers ({data?.window.months || months} mo)</div>
                    <div className="space-y-1">
                        {(data?.top_late || []).slice(0, 5).map((e) => (
                            <div key={e.employee_id} className="flex items-center justify-between text-sm rounded-md border border-slate-100 px-2.5 py-1.5 cursor-pointer hover:bg-slate-50"
                                 onClick={() => navigate(`/hris/employees/${e.employee_id}`)}>
                                <div>
                                    <span className="font-medium">{e.name}</span>
                                    <span className="text-xs text-muted-foreground"> · {e.generation.split(" ")[0] === "Unspecified" ? e.department : `${e.generation.split(" (")[0]} · ${e.department}`}</span>
                                </div>
                                <div className="text-xs font-mono shrink-0">{e.late_count}× · {e.late_minutes.toLocaleString()} min</div>
                            </div>
                        ))}
                        {!data?.top_late?.length && <div className="text-xs text-muted-foreground">No late arrivals recorded.</div>}
                    </div>
                </div>
                <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-1">🚫 Most absences ({data?.window.months || months} mo)</div>
                    <div className="space-y-1">
                        {(data?.top_absent || []).slice(0, 5).map((e) => (
                            <div key={e.employee_id} className="flex items-center justify-between text-sm rounded-md border border-slate-100 px-2.5 py-1.5 cursor-pointer hover:bg-slate-50"
                                 onClick={() => navigate(`/hris/employees/${e.employee_id}`)}>
                                <div>
                                    <span className="font-medium">{e.name}</span>
                                    <span className="text-xs text-muted-foreground"> · {e.generation.split(" (")[0]} · {e.department}</span>
                                </div>
                                <div className="text-xs font-mono shrink-0">{e.absences} day{e.absences === 1 ? "" : "s"}</div>
                            </div>
                        ))}
                        {!data?.top_absent?.length && <div className="text-xs text-muted-foreground">No unexplained absences — nice.</div>}
                    </div>
                </div>
            </div>
        </Surface>
    );
}

export default function Dashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
    }, []);
    if (!data) return <div className="text-sm text-muted-foreground">Loading dashboard…</div>;
    const go = (to) => () => navigate(to);

    const trend = data.payroll_cost_trend || [];
    const typeData = (data.type_breakdown || []).map((t) => ({ name: t._id || "—", value: t.count }));

    return (
        <div>
            <PageHeader
                title="Executive Dashboard"
                subtitle="Headcount, payroll cost, attendance, and compliance overview for the active company."
            />
            <InstallPWA variant="banner" />
            <WageOrderBanner />
            <ContractAlertsBanner />
            <AnnouncementBoard />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="cursor-pointer" onClick={go("/hris/employees")}><Stat label="Active Headcount" value={data.headcount} icon={Users} tone="primary" /></div>
                <div className="cursor-pointer" onClick={go("/timekeeping/attendance")}><Stat label="Present Today" value={data.present_today} icon={Clock} /></div>
                <div className="cursor-pointer" onClick={go("/timekeeping/leaves?status=submitted")}><Stat label="Pending Leaves" value={data.pending_leaves} icon={FileText} /></div>
                <div className="cursor-pointer" onClick={go("/timekeeping/overtime?status=submitted")}><Stat label="Pending OT Approvals" value={data.pending_ot} icon={TrendingUp} /></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
                <Surface className="p-4 lg:col-span-2">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display font-semibold">Payroll Cost Trend (Net Pay)</h3>
                        <div className="text-xs text-muted-foreground">Last {trend.length} periods</div>
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="#e5e7eb" vertical={false} />
                            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => formatPHP(v)} />
                            <Bar dataKey="amount" fill="#0F4C81" radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Surface>
                <Surface className="p-4">
                    <h3 className="font-display font-semibold mb-3">Employment Type</h3>
                    <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                            <Pie data={typeData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                                {typeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-1 mt-2">
                        {typeData.map((t, i) => (
                            <div key={t.name} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                    <span className="size-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                                    <span className="capitalize">{t.name.replace(/_/g, " ")}</span>
                                </div>
                                <span className="font-mono">{t.value}</span>
                            </div>
                        ))}
                    </div>
                </Surface>
            </div>
            <DemographicsSection />
            <TardinessExplorer />
            <Surface className="mt-3 p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display font-semibold">Recent Payroll Runs</h3>
                </div>
                <table className="w-full dense-table">
                    <thead>
                        <tr>
                            <th>Period</th>
                            <th>Status</th>
                            <th className="num">Employees</th>
                            <th className="num">Gross</th>
                            <th className="num">Deductions</th>
                            <th className="num">Net</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data.recent_runs || []).map((r) => (
                            <tr key={r.id} className="cursor-pointer" onClick={() => navigate(`/payroll/runs/${r.id}`)} data-testid={`dashboard-run-${r.id}`}>
                                <td className="font-mono">{r.period_start} → {r.period_end}</td>
                                <td><StatusBadge value={r.status} /></td>
                                <td className="num">{r.employee_count}</td>
                                <td className="num">{formatPHP(r.total_gross)}</td>
                                <td className="num">{formatPHP(r.total_deductions)}</td>
                                <td className="num font-semibold">{formatPHP(r.total_net)}</td>
                            </tr>
                        ))}
                        {!data.recent_runs?.length && <tr><td colSpan={6} className="text-center text-muted-foreground py-6">No payroll runs yet.</td></tr>}
                    </tbody>
                </table>
            </Surface>
        </div>
    );
}
